import { CleaveTextField } from '@/global/components/Form/CleaveTextField'
// O `phone` do cleave lê o formatador de `window.Cleave.AsYouTypeFormatter`,
// que só existe depois deste import; sem ele a máscara abaixo lança.
import 'cleave.js/dist/addons/cleave-phone.br'
import { cn } from '@/lib/utils'
import { cva, type VariantProps } from 'class-variance-authority'
import type { CleaveOptions } from 'cleave.js/options'
import type { Props as CleaveProps } from 'cleave.js/react/props'
import * as React from 'react'

/**
 * Caixa do campo — carrega fundo, underline e estado visual.
 *
 * `filled` replica o `variant="filled"` do MUI: fundo sutil, cantos
 * arredondados só no topo e underline de 1px que vira 2px no foco/erro. Com
 * rótulo a caixa vira coluna — rótulo de 12px em cima, valor de 16px embaixo,
 * 56px de altura — que é a geometria do campo médio do MUI. É o que mantém os
 * campos novos alinhados com os que ainda são MUI na mesma tela.
 *
 * Fundo e raio copiam o override de `MuiFilledInput` do `theme.ts` (`muted` a
 * 80%, 95% no hover/foco, `0.28rem` nos quatro cantos) — é o que faz o campo
 * novo encostar no MUI que ainda existe na mesma tela.
 *
 * O underline é um pseudo-elemento absoluto (`after:`), não `border-b`, para
 * que a troca de 1px → 2px não altere a caixa e desloque o layout.
 */
const inputVariants = cva(
    [
        'group/campo relative isolate flex w-full text-foreground',
        'transition-[color,background-color,border-color,box-shadow]',
        'data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
    ],
    {
        variants: {
            variant: {
                filled: [
                    'rounded-sm border-0 px-3 rounded-b-none',
                    'bg-muted/80 hover:bg-muted/95 focus-within:bg-muted/95',
                    "after:pointer-events-none after:absolute after:content-['']",
                    'after:inset-x-0 after:bottom-0 after:h-px',
                    'after:bg-foreground/40 hover:after:bg-foreground',
                    'after:transition-[height,background-color]',
                    'focus-within:after:h-0.5 focus-within:after:bg-primary',
                    'focus-within:hover:after:bg-primary',
                    'data-[invalid]:after:h-0.5 data-[invalid]:after:bg-destructive',
                    'data-[invalid]:hover:after:bg-destructive',
                ],
                outlined: [
                    'rounded-sm px-3 shadow-xs',
                    'border border-input bg-transparent dark:bg-input/30',
                    'focus-within:border-ring focus-within:ring-[3px] focus-within:ring-ring/50',
                    'data-[invalid]:border-destructive data-[invalid]:ring-destructive/20',
                    'dark:data-[invalid]:ring-destructive/40',
                ],
            },
            /** Rótulo dentro da caixa — muda altura e tipografia, como no MUI. */
            comRotulo: {
                true: 'flex-col justify-center',
                false: 'items-center',
            },
            /** As duas alturas do MUI: 56px e 48px, com ou sem rótulo. */
            size: {
                medium: '',
                small: '',
            },
        },
        compoundVariants: [
            {
                variant: 'filled',
                comRotulo: true,
                size: 'medium',
                class: 'h-14 pt-[7px] pb-2 text-base',
            },
            {
                variant: 'filled',
                comRotulo: true,
                size: 'small',
                class: 'h-12 py-1 text-base',
            },
            {
                variant: 'filled',
                comRotulo: false,
                size: 'medium',
                class: 'h-14 text-base',
            },
            {
                variant: 'filled',
                comRotulo: false,
                size: 'small',
                class: 'h-12 text-base',
            },
            {
                variant: 'outlined',
                comRotulo: true,
                size: 'medium',
                class: 'h-14 py-2 text-base',
            },
            {
                variant: 'outlined',
                comRotulo: true,
                size: 'small',
                class: 'h-12 py-1 text-base',
            },
            { variant: 'outlined', comRotulo: false, class: 'h-9 text-sm' },
        ],
        defaultVariants: {
            variant: 'filled',
            comRotulo: false,
            size: 'medium',
        },
    },
)

/**
 * Rótulo dentro da caixa. Segue a cor do estado do campo — cinza em repouso,
 * primário no foco, destrutivo no erro — via o grupo `campo` da caixa.
 *
 * `shrink-0` é obrigatório: `truncate` traz `overflow: hidden`, o que zera o
 * mínimo automático do item flex. Sem ele, um adorno mais alto que a linha do
 * valor — o `IconeAjuda`, que tem 40px — estoura a altura da caixa e o rótulo
 * é o único item que aceita encolher: some por inteiro, sem aviso.
 */
const rotuloCampoClass = [
    'max-w-full shrink-0 truncate text-xs leading-[17.25px] font-normal select-none',
    'origin-left text-muted-foreground',
    // `translate`/`scale` entram na lista porque o Tailwind 4 escreve as
    // propriedades individuais, e não `transform` — sem elas o rótulo saltaria
    // entre os dois tamanhos em vez de deslizar.
    'transition-[color,max-width,transform,translate,scale] duration-200 ease-out',
    'group-focus-within/campo:text-primary',
    'group-data-[invalid]/campo:text-destructive',
]

/**
 * Rótulo no lugar do valor — o estado "não encolhido" do MUI. Sem valor, sem
 * foco e sem placeholder não há nada para mostrar na linha do valor, então o
 * rótulo desce e cresce até a tipografia do valor (12px × 1.3333 = 16px, com a
 * entrelinha indo de 17.25px para os 23px da linha do valor).
 *
 * É `translate`+`scale`, não `top`/`font-size`: transformação não reflui, então
 * a caixa continua com os mesmos 56px/48px nos dois estados e a troca é suave.
 * O deslocamento de 12px leva o centro do rótulo ao centro da caixa em todas as
 * combinações de variante e tamanho (a maior diferença é de 0,5px, no `small`).
 *
 * `origin-left` mantém a borda esquerda no lugar durante a escala, e o
 * `max-w-[75%]` é o que sobra de largura útil depois dela (75% × 1.3333 = 100%)
 * — sem ele o texto ampliado vazaria para fora da caixa.
 */
const rotuloGrandeClass =
    'max-w-[75%] translate-y-3 scale-[1.3333] text-foreground'

/**
 * O mesmo estado, decidido em CSS. O `<input>`/`<textarea>` é usado controlado
 * e não controlado, então quem sabe se há valor é o próprio DOM:
 * `:placeholder-shown` responde por "vazio" e `:focus-within` pelo foco.
 *
 * Só é aplicado quando o campo não tem placeholder nenhum — ver `rotuloFlutua`.
 */
const rotuloGrandeCssClass = [
    'group-[:not(:focus-within):has(:placeholder-shown)]/campo:max-w-[75%]',
    'group-[:not(:focus-within):has(:placeholder-shown)]/campo:translate-y-3',
    'group-[:not(:focus-within):has(:placeholder-shown)]/campo:scale-[1.3333]',
    'group-[:not(:focus-within):has(:placeholder-shown)]/campo:text-foreground',
]

/**
 * Tipos que desenham um placeholder próprio do navegador (`dd/mm/aaaa`,
 * `--:--`). Para eles `:placeholder-shown` nunca casa e sempre há algo escrito
 * na linha do valor, então o rótulo nasce encolhido.
 */
const tiposComPlaceholderProprio = new Set([
    'date',
    'time',
    'month',
    'week',
    'datetime-local',
])

/**
 * Linha do valor: adornos + controle, na altura de linha do MUI (23px).
 *
 * `min-h-[23px]`, e não um `min-h-0` puro: continua deixando a linha ceder
 * quando o adorno é mais alto que ela — sem o mínimo explícito o piso do flex
 * seria o tamanho do adorno, o `IconeAjuda` tem 40px e o excedente empurraria o
 * rótulo para fora da caixa — mas agora com piso na própria entrelinha. Sem
 * esse piso a linha desaparece quando não há valor nem placeholder (Select e
 * Combobox vazios), o `justify-center` da caixa centraliza o rótulo sozinho e
 * ele passa a partir de um lugar diferente em cada estado.
 *
 * `relative z-10`: o rótulo grande é transformado, o que o pinta na camada dos
 * elementos posicionados — sem isto ele passaria por cima dos adornos e da seta
 * do Combobox/Select. O `isolate` da caixa segura esse z-index dentro do campo.
 */
const linhaControleClass =
    'relative z-10 flex w-full min-h-[23px] min-w-0 items-center gap-2 leading-[23px]'

const adornmentClass = 'flex shrink-0 items-center text-muted-foreground'

/**
 * Máscara implícita do `type="tel"`: telefone brasileiro, com e sem o nono
 * dígito. Fica aqui, e não repetida em cada formulário, para todo telefone do
 * sistema ser digitado do mesmo jeito.
 */
const MASCARA_TELEFONE: CleaveOptions = { phone: true, phoneRegionCode: 'BR' }

const controlClass = [
    'w-full min-w-0 flex-1 bg-transparent',
    'text-inherit',
    'border-none outline-none',
    'placeholder:text-muted-foreground',
    'selection:bg-primary selection:text-primary-foreground',
    'disabled:cursor-not-allowed',
]

type CamposComuns = VariantProps<typeof inputVariants> & {
    /** Rótulo dentro da caixa, acima do valor — o do `filled` do MUI. */
    label?: React.ReactNode
    /** Conteúdo à esquerda do campo (substitui o `InputAdornment` do MUI). */
    startAdornment?: React.ReactNode
    /** Conteúdo à direita do campo (substitui o `InputAdornment` do MUI). */
    endAdornment?: React.ReactNode
    /**
     * Máscara do cleave.js. Presente, o controle vira um `Cleave` no lugar do
     * `<input>` — é assim que o campo valida/formata enquanto se digita.
     *
     * `type="tel"` já traz a máscara de telefone; passe `options` só para
     * trocá-la por outra.
     */
    options?: CleaveOptions
    /**
     * Clique na caixa inteira — rótulo, adornos e a sobra dos 56px, não só na
     * faixa do controle. É o comportamento do `onClick` do `TextField` do MUI,
     * que também ficava no root, e é o que faz um campo somente-leitura com
     * adorno de ação (upload, abrir seletor, abrir diálogo) responder onde o
     * usuário mira: o ícone.
     *
     * Como o handler fica em um ancestral do controle e dos adornos, qualquer
     * botão dentro do campo precisa de `stopPropagation()` para não disparar
     * as duas ações no mesmo clique.
     */
    onClick?: React.MouseEventHandler<HTMLDivElement>
}

/**
 * `size` do HTML sai fora: aqui é a variante de altura, como no MUI.
 * `onClick` sai fora porque é redeclarado como clique do root em `CamposComuns`.
 */
type PropsElemento<E extends 'input' | 'textarea'> = Omit<
    React.ComponentPropsWithoutRef<E>,
    'size' | 'onClick'
>

type InputProps =
    | (CamposComuns & PropsElemento<'input'> & { multiline?: false })
    | (CamposComuns & PropsElemento<'textarea'> & { multiline: true })

/**
 * `className` é aplicado no wrapper, não no controle — intencional: espelha o
 * root do `TextField` do MUI, que é onde os call-sites passam largura, margem e
 * visibilidade. Não "corrigir" movendo para o controle. Para mirar o controle
 * em si, use o seletor `[&_input]:…` / `[&_textarea]:…`.
 *
 * `onClick` segue a mesma regra e pelo mesmo motivo: vai no wrapper, como no
 * root do `TextField` do MUI. Ver o JSDoc da prop.
 *
 * A `ref` aponta para o `<input>`/`<textarea>` (react-hook-form registra por ela).
 */
const Input = React.forwardRef<
    HTMLInputElement | HTMLTextAreaElement,
    InputProps
>(
    (
        {
            className,
            variant,
            size,
            label,
            startAdornment,
            endAdornment,
            onClick,
            ...props
        },
        ref,
    ) => {
        const { multiline, options, ...resto } = props as InputProps & {
            multiline?: boolean
        }
        const ariaInvalid = resto['aria-invalid']
        const invalid =
            ariaInvalid !== undefined &&
            ariaInvalid !== false &&
            ariaInvalid !== 'false'
        const temRotulo = label !== undefined && label !== ''
        const tipo = (resto as { type?: string }).type
        const temPlaceholderProprio =
            tipo !== undefined && tiposComPlaceholderProprio.has(tipo)
        const temPlaceholder =
            typeof resto.placeholder === 'string' && resto.placeholder !== ''
        /**
         * Com placeholder — do call-site ou do próprio controle — já há algo
         * escrito na linha do valor, e o rótulo nasce encolhido. Sem ele, o
         * rótulo flutua: um placeholder de um espaço é injetado só para dar ao
         * CSS o `:placeholder-shown` que sinaliza campo vazio.
         */
        const rotuloFlutua =
            temRotulo && !temPlaceholder && !temPlaceholderProprio
        const mascara =
            options ?? (tipo === 'tel' ? MASCARA_TELEFONE : undefined)
        const propsControle = rotuloFlutua
            ? { ...resto, placeholder: ' ' }
            : resto

        return (
            <div
                data-slot="input"
                data-invalid={invalid || undefined}
                data-disabled={resto.disabled || undefined}
                onClick={onClick}
                className={cn(
                    inputVariants({ variant, size, comRotulo: temRotulo }),
                    // Sem altura mínima: quem manda na altura do multiline é
                    // o `rows` do `<textarea>`. Um `min-h` fixo brigaria com
                    // ele — venceria o `rows` menor e ignoraria o maior — e
                    // deixaria a caixa sem relação com o número de linhas
                    // pedido.
                    multiline && 'h-auto items-start py-2',
                    className,
                )}
            >
                {temRotulo && (
                    <label
                        data-slot="input-label"
                        htmlFor={resto.id}
                        className={cn(
                            rotuloCampoClass,
                            rotuloFlutua && rotuloGrandeCssClass,
                        )}
                    >
                        {label}
                    </label>
                )}
                <div
                    className={cn(
                        linhaControleClass,
                        multiline && 'items-start',
                    )}
                >
                    {startAdornment && (
                        <span
                            data-slot="input-start-adornment"
                            className={cn(adornmentClass, 'mt-1')}
                        >
                            {startAdornment}
                        </span>
                    )}
                    {multiline ? (
                        <textarea
                            // Três linhas é o tamanho padrão do campo de texto
                            // longo do app; o call-site continua podendo pedir
                            // outro pelo `rows`, que vem depois no espalhamento.
                            rows={3}
                            ref={ref as React.Ref<HTMLTextAreaElement>}
                            data-slot="input-control"
                            className={cn(controlClass, 'resize-y')}
                            {...(propsControle as PropsElemento<'textarea'>)}
                        />
                    ) : mascara ? (
                        // `Props` do `cleave.js/react` declara `onChange` como
                        // o handler com `rawValue` no `target`, incompatível
                        // com o `ChangeEventHandler` do call-site; o cast do
                        // objeto espalhado resolve sem `any` solto. `data-slot`
                        // fica fora dele porque `Props` não tem index
                        // signature e a checagem de propriedade excedente o
                        // recusaria dentro do literal.
                        <CleaveTextField
                            ref={ref as React.Ref<HTMLInputElement>}
                            data-slot="input-control"
                            className={cn(controlClass)}
                            {...({
                                options: mascara,
                                ...propsControle,
                            } as CleaveProps)}
                        />
                    ) : (
                        <input
                            ref={ref as React.Ref<HTMLInputElement>}
                            data-slot="input-control"
                            className={cn(controlClass)}
                            {...(propsControle as PropsElemento<'input'>)}
                        />
                    )}
                    {endAdornment && (
                        <span
                            data-slot="input-end-adornment"
                            className={cn(adornmentClass, 'mb-2')}
                        >
                            {endAdornment}
                        </span>
                    )}
                </div>
            </div>
        )
    },
)
Input.displayName = 'Input'

export {
    Input,
    inputVariants,
    linhaControleClass,
    rotuloCampoClass,
    rotuloGrandeClass,
}
export type { InputProps }
